import React from "react";
import styled from "@emotion/styled";
import { Field } from "formik";
import { tokens } from "../../../design-system/tokens";

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing.xs};
`;

const Label = styled.label<{ required?: boolean }>`
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
  color: ${tokens.colors.basics[1300]};

  ${(props) =>
    props.required &&
    `
    &::after {
      content: ' *';
      color: ${tokens.colors.semantic.error};
    }
  `}
`;

const StyledField = styled(Field)`
  padding: ${tokens.spacing.sm} ${tokens.spacing.md};
  border: 2px solid ${tokens.colors.basics[500]};
  border-radius: ${tokens.borderRadius.md};
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.basics[1400]};
  background-color: ${tokens.colors.basics[0]};
  transition: ${tokens.transitions.normal};
  font-family: ${tokens.typography.fontFamily.primary};

  &:focus {
    outline: none;
    border-color: ${tokens.colors.blue[900]};
    box-shadow: 0 0 0 3px ${tokens.colors.blue[100]};
  }

  &::placeholder {
    color: ${tokens.colors.basics[1000]};
  }

  &:disabled {
    background-color: ${tokens.colors.basics[200]};
    color: ${tokens.colors.basics[1100]};
    cursor: not-allowed;
  }
`;

const HelpText = styled.div`
  color: ${tokens.colors.basics[1100]};
  font-size: ${tokens.typography.fontSize.xs};
  margin-top: ${tokens.spacing.xs};
`;

export interface FormFieldProps {
  id: string;
  name: string;
  label: string;
  type?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  helpText?: string;
  className?: string;
  as?: "input" | "textarea" | "select";
  children?: React.ReactNode; // Para options en select
}

export const FormField: React.FC<FormFieldProps> = ({
  id,
  name,
  label,
  type = "text",
  placeholder,
  disabled = false,
  required = false,
  helpText,
  className,
  as = "input",
  children,
}) => {
  return (
    <FieldContainer className={className}>
      <Label htmlFor={id} required={required}>
        {label}
      </Label>
      <StyledField
        id={id}
        name={name}
        type={type}
        as={as}
        placeholder={placeholder}
        disabled={disabled}
      >
        {children}
      </StyledField>
      {helpText && <HelpText>{helpText}</HelpText>}
    </FieldContainer>
  );
};

export default FormField;
